/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useCallback, useContext, useState } from 'react';
import Button from '@broadleaf/admin-components/dist/common/elements/Button';
import type {
  IMetadata,
  IMetadataAction
} from '@broadleaf/admin-components/dist/types/metadata';
import type { ICollectionStateHook } from '@broadleaf/admin-components/dist/types/collection';
import { IConsolidatedOrder } from '../../../../utils/RmFulfillmentUtils';
import InvoiceDetailsModal from '../../InvoiceDetailsModal';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import useContextParams from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/hooks/useContextParams';
import log from '@broadleaf/admin-components/dist/common/utils/log';
import {
  IconButton,
  ToastContainerContext
} from '@broadleaf/admin-components/dist/common';
import {
  getConsolidatedOrderEndpoint,
  getDownloadOrderInvoiceEndpoint
} from '../../../utils/InvoicesOperationUtils';
import { getSecureAssetUrl } from '@src/utils/auth-utils';

const logger = log.getLogger(
  'oms.components.InvoicesView.ReadyToSubmitGrid.action-components.GetInvoicePdfAction'
);
export const GetInvoicePdfAction: React.FC<GetInvoiceDetailsActionProps> = props => {
  const [loading, setLoading] = useState<boolean>(false);
  const contextParams = useContextParams(props.metadata);
  const toast = useContext(ToastContainerContext);

  const disabled = !props.row || props.disabled || props.loading || loading;

  const fetchOrderInvoice = useCallback(async () => {
    setLoading(true);
    try {
      const downloadOrderInvoiceEndpoint = getDownloadOrderInvoiceEndpoint(
        props.row.id
      );

      const { data: invoiceAsset } = await request(
        { method: 'get', ...downloadOrderInvoiceEndpoint },
        contextParams
      );

      let invoiceUrl = invoiceAsset?.contentUrl;

      invoiceUrl = await getSecureAssetUrl(invoiceUrl);

      if (invoiceUrl) {
        window.open(invoiceUrl, '_blank');
      }
    } catch (err) {
      if (err.cancelled) {
        return;
      }
      const errorMessage = `An error occurred while loading invoice: ${err.message}`;
      logger.error(errorMessage);
      toast.addToast(errorMessage, {
        autoClose: 5000,
        closeOnClick: true,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  }, [props.row.id, contextParams, toast]);

  function handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    fetchOrderInvoice();
  }

  return (
    <IconButton
      disabled={disabled}
      onClick={handleClick}
      name={'download'}
      color={'solid'}
      label={props.actionDefinition.label}
    />
  );
};

export interface GetInvoiceDetailsActionProps {
  metadata: IMetadata;
  actionDefinition?: IMetadataAction;
  collectionState?: ICollectionStateHook;
  refreshUntilUpdated?: (items: any[]) => void;
  disabled?: boolean;
  loading?: boolean;
  row?: any;
  rowIndex?: number;
}

export default GetInvoicePdfAction;
